"use client";

import { useState } from "react";
import {
  ErrorNotice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import { BasketMode } from "@/components/tools/econ-macro/index-basket";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  annualisedInflation,
  inflationBetween,
} from "@/lib/tool-kit/econ-macro/price-index";

/** English strings. A translated content file overrides these through `ui`. */
export const PRICE_INDEX_CALCULATOR_STRINGS = {
  modeLabel: "What do you have?",
  modeBasket: "Prices and quantities of a basket of goods",
  modeIndexes: "Two index values (such as CPI)",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  basketHeading: "Your basket",
  basketHint:
    "List each good with its price and the quantity bought in the base period and in the current period. The base period is the one the index compares against.",
  itemHeading: "Good {n}",
  itemName: "Name",
  itemDefault: "Good {n}",
  sampleBread: "Bread (loaf)",
  sampleMilk: "Milk (gallon)",
  sampleGas: "Gasoline (gallon)",
  sampleMovie: "Movie ticket",
  basePrice: "Price in the base period (p₀)",
  baseQuantity: "Quantity in the base period (q₀)",
  currentPrice: "Price in the current period (p₁)",
  currentQuantity: "Quantity in the current period (q₁)",
  addItem: "Add a good",
  removeItem: "Remove good {n}",
  invalidBasket:
    "Every good needs a price above zero in both periods and a quantity of zero or more, and the base-period basket needs some spending. Check good {n}.",
  emptyBasket:
    "Enter at least one good with a base-period quantity above zero.",
  resultsHeading: "Results",
  laspeyres: "Laspeyres index",
  paasche: "Paasche index",
  fisher: "Fisher ideal index",
  inflationLaspeyres: "Price change (Laspeyres)",
  inflationPaasche: "Price change (Paasche)",
  inflationFisher: "Price change (Fisher)",
  costHeading: "What each basket costs",
  basketColumn: "Basket",
  atBaseColumn: "At base-period prices",
  atCurrentColumn: "At current-period prices",
  rowBaseBasket: "Base-period basket (q₀)",
  rowCurrentBasket: "Current-period basket (q₁)",
  itemsHeading: "How each good contributes",
  goodColumn: "Good",
  relativeColumn: "Price relative (p₁ ÷ p₀)",
  shareColumn: "Share of base spending",
  contributionColumn: "Contribution to the Laspeyres change",
  workingHeading: "Working",
  workLaspeyres: "Laspeyres = Σp₁q₀ ÷ Σp₀q₀ × 100 = {a} ÷ {b} × 100 = {l}",
  workPaasche: "Paasche = Σp₁q₁ ÷ Σp₀q₁ × 100 = {c} ÷ {d} × 100 = {p}",
  workFisher: "Fisher = √(Laspeyres × Paasche) = √({l} × {p}) = {f}",
  workChange: "Price change = index ÷ 100 − 1 = {l} ÷ 100 − 1 = {change}",
  compareNote:
    "The Laspeyres index keeps the old basket, so it ignores buyers switching away from goods that got dearer and tends to read higher. The Paasche index uses the new basket and tends to read lower. The Fisher index sits between them. Consumer price indexes such as the U.S. CPI price a fixed basket of goods, which makes them Laspeyres-type indexes.",
  earlierIndex: "Index value in the earlier period",
  laterIndex: "Index value in the later period",
  yearsBetween: "Years between the two periods",
  yearsHint:
    "Used for the average yearly rate. Leave it at 1 if the two values are one year apart.",
  invalidIndexes: "Enter two index values above zero.",
  invalidYears: "Enter a number of years above zero.",
  inflationRate: "Inflation over the whole period",
  annualRate: "Average yearly inflation",
  powerCard: "What 100 then costs now",
  purchasingCard: "Purchasing power of 100 now",
  workIndexInflation:
    "Inflation = (later − earlier) ÷ earlier = ({later} − {earlier}) ÷ {earlier} = {rate}",
  workIndexAnnual:
    "Average yearly rate = (later ÷ earlier)^(1 ÷ years) − 1 = ({later} ÷ {earlier})^(1 ÷ {years}) − 1 = {annual}",
  workIndexCost:
    "What cost 100 in the earlier period now costs 100 × {later} ÷ {earlier} = {cost}",
  indexNote:
    "An index number has no units; only the ratio between two values means anything. The base period is wherever the series equals 100.",
} as const;

export type Strings = typeof PRICE_INDEX_CALCULATOR_STRINGS;
type Mode = "basket" | "indexes";

function IndexesMode({ strings }: { strings: Strings }) {
  const fmt = useToolNumber();
  const [earlier, setEarlier] = useState("258.811");
  const [later, setLater] = useState("321.943");
  const [years, setYears] = useState("5");
  const a = parseNumberInput(earlier);
  const b = parseNumberInput(later);
  const n = parseNumberInput(years);
  let problem: string | null = null;
  if (a === null || b === null || a <= 0 || b <= 0) {
    problem = strings.invalidIndexes;
  } else if (n === null || n <= 0) {
    problem = strings.invalidYears;
  }
  const num = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  const pct = (value: number) =>
    `${fmt(value * 100, { maximumFractionDigits: 2 })}%`;
  const ready = a !== null && b !== null && n !== null && !problem;
  const rate = ready ? inflationBetween(a, b) : 0;
  const annual = ready ? annualisedInflation(a, b, n) : 0;
  const line = "font-mono text-muted-foreground text-sm leading-relaxed";
  return (
    <>
      <section className={`${CARD} space-y-4`}>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id="pi-earlier"
            label={strings.earlierIndex}
            numeric
            onChange={setEarlier}
            value={earlier}
          />
          <TextField
            id="pi-later"
            label={strings.laterIndex}
            numeric
            onChange={setLater}
            value={later}
          />
          <TextField
            hint={strings.yearsHint}
            id="pi-years"
            label={strings.yearsBetween}
            numeric
            onChange={setYears}
            value={years}
          />
        </div>
      </section>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {ready && a !== null && b !== null && n !== null ? (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.inflationRate}
                value={pct(rate)}
              />
              <StatCard label={strings.annualRate} value={pct(annual)} />
              <StatCard label={strings.powerCard} value={num((100 * b) / a)} />
              <StatCard
                label={strings.purchasingCard}
                value={num((100 * a) / b)}
              />
            </StatGrid>
            <div className={`${CARD} space-y-1`}>
              <p className={line}>
                {t(strings.workIndexInflation, {
                  later: num(b),
                  earlier: num(a),
                  rate: pct(rate),
                })}
              </p>
              <p className={line}>
                {t(strings.workIndexAnnual, {
                  later: num(b),
                  earlier: num(a),
                  years: num(n),
                  annual: pct(annual),
                })}
              </p>
              <p className={line}>
                {t(strings.workIndexCost, {
                  later: num(b),
                  earlier: num(a),
                  cost: num((100 * b) / a),
                })}
              </p>
            </div>
            <p className="text-muted-foreground text-sm">{strings.indexNote}</p>
          </>
        ) : null}
      </section>
    </>
  );
}

export default function PriceIndexCalculatorWidget() {
  const strings = useToolUi(PRICE_INDEX_CALCULATOR_STRINGS);
  const [mode, setMode] = useState<Mode>("basket");
  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="pi-mode"
        onChange={setMode}
        options={[
          { value: "basket", label: strings.modeBasket },
          { value: "indexes", label: strings.modeIndexes },
        ]}
        value={mode}
      />
      {mode === "basket" ? (
        <BasketMode strings={strings} />
      ) : (
        <IndexesMode strings={strings} />
      )}
    </div>
  );
}
